import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StarSpinner } from "@/components/StarSpinner";

const BUCKET_META = {
  90: { label: "90-Day", tone: "Advance notice", cls: "text-navy" },
  60: { label: "60-Day", tone: "Sixty-day notice", cls: "text-brass2" },
  30: { label: "30-Day", tone: "Final notice window", cls: "text-oxblood" },
};

export default function AdminRenewalDigest() {
  const [preview, setPreview] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get("/admin/renewal-digest/preview"),
        api.get("/admin/renewal-digest/runs"),
      ]);
      setPreview(p.data);
      setRuns(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dispatch = async () => {
    if (!window.confirm(
      "This will send LIVE emails to every client with a renewal in the 90/60/30 window, plus firm alerts to info@LoneStarCounsel.com. Continue?"
    )) return;
    setDispatching(true);
    try {
      const { data } = await api.post("/admin/renewal-digest/dispatch");
      toast.success(`Sent ${data.sent} client notice(s) + ${data.firm_alerts} firm alert(s)`);
      await load();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      toast.error(detail || "Dispatch failed");
    } finally {
      setDispatching(false);
    }
  };

  if (loading || !preview) {
    return (
      <div className="mt-10" data-testid="admin-digest-loading">
        <StarSpinner label="Computing renewal windows" />
      </div>
    );
  }

  const enabled = preview.dispatch_enabled;

  return (
    <div className="mt-10" data-testid="admin-digest-tab">
      {/* Header + dispatch controls */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 hairline-bottom pb-6">
        <div>
          <div className="kicker">Renewal Digest</div>
          <h2 className="mt-3 font-serif text-3xl text-navy">
            90 · 60 · 30-day renewal windows
          </h2>
          <p className="mt-3 max-w-2xl text-slate2 font-light">
            Automated notices for IP assets approaching a maintenance deadline. Client emails
            and internal firm alerts are drafted below. Dispatch is gated until email is activated.
          </p>
          {preview.next_scheduled_run && (
            <p
              className="mt-4 text-sm font-cormorant italic text-brass2"
              data-testid="admin-digest-next-run"
            >
              Next automatic dispatch: {new Date(preview.next_scheduled_run).toLocaleString("en-US", {
                weekday: "long", year: "numeric", month: "long", day: "numeric",
                hour: "numeric", minute: "2-digit", timeZoneName: "short",
              })}
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate2 mb-2">
            Email Dispatch Status
          </div>
          <div
            className={`inline-block px-4 py-2 text-xs uppercase tracking-[0.22em] font-medium ${
              enabled ? "bg-hunter/15 text-hunter" : "bg-oxblood/10 text-oxblood"
            }`}
            data-testid="admin-digest-status"
          >
            {enabled ? "Live" : "Preview Only (Not Activated)"}
          </div>
          <button
            onClick={dispatch}
            disabled={!enabled || dispatching || preview.total_notices === 0}
            className="mt-4 btn-primary disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-3 ml-auto"
            data-testid="admin-digest-dispatch"
          >
            {dispatching ? (
              <>
                <StarSpinner size="sm" label="" />
                <span>Sending…</span>
              </>
            ) : (
              <span>Dispatch Now ({preview.total_notices})</span>
            )}
          </button>
        </div>
      </div>

      {/* Bucket summary */}
      <div className="mt-8 grid grid-cols-3 gap-px bg-brass/30" data-testid="admin-digest-buckets">
        {[90, 60, 30].map((b) => {
          const m = BUCKET_META[b];
          return (
            <div key={b} className="bg-ivory p-8" data-testid={`admin-digest-bucket-${b}`}>
              <div className={`kicker ${m.cls}`}>{m.label} out</div>
              <div className={`mt-3 font-serif text-6xl leading-none ${m.cls}`}>
                {preview.buckets[String(b)] || 0}
              </div>
              <div className="mt-3 text-slate2 font-light text-sm">
                {m.tone} &nbsp;·&nbsp; asset(s) queued
              </div>
            </div>
          );
        })}
      </div>

      {/* Firm alerts preview */}
      {preview.firm_alerts.length > 0 && (
        <div className="mt-12">
          <div className="kicker">Internal Firm Alerts</div>
          <h3 className="mt-2 font-serif text-2xl text-navy">
            {preview.firm_alerts.length} alert(s) to {preview.firm_alerts[0]?.recipient}
          </h3>
          <div className="mt-6 bg-brass/30 grid grid-cols-1 gap-px" data-testid="admin-digest-firm-alerts">
            {preview.firm_alerts.map((a) => (
              <div
                key={`firm-${a.bucket}`}
                className="bg-ivory p-6 grid grid-cols-1 md:grid-cols-12 gap-4"
                data-testid={`admin-digest-firm-${a.bucket}`}
              >
                <div className="md:col-span-2">
                  <div className={`kicker ${BUCKET_META[a.bucket].cls}`}>
                    {BUCKET_META[a.bucket].label}
                  </div>
                </div>
                <div className="md:col-span-8 font-serif text-navy">{a.subject}</div>
                <div className="md:col-span-2 md:text-right text-xs text-slate2 uppercase tracking-[0.2em]">
                  {a.asset_count} asset(s)
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client notices preview */}
      <div className="mt-12">
        <div className="kicker">Client Notices</div>
        <h3 className="mt-2 font-serif text-2xl text-navy">
          {preview.client_notices.length} client email(s) drafted
        </h3>
        {preview.client_notices.length === 0 ? (
          <div className="mt-6 p-12 border border-transparent bg-linen/40 text-center font-serif italic text-slate2" data-testid="admin-digest-empty">
            No renewal-window notices at this time.
          </div>
        ) : (
          <div className="mt-6 bg-brass/30 grid grid-cols-1 gap-px" data-testid="admin-digest-client-notices">
            <div className="bg-linen/60 grid grid-cols-12 gap-4 px-6 py-4 text-xs uppercase tracking-[0.2em] text-slate2 font-medium">
              <div className="col-span-1">Bucket</div>
              <div className="col-span-3">Client</div>
              <div className="col-span-4">Subject</div>
              <div className="col-span-2">Asset</div>
              <div className="col-span-1 text-center">Certificate</div>
              <div className="col-span-1 text-right">Days</div>
            </div>
            {preview.client_notices.map((n, i) => (
              <div
                key={`${n.recipient}-${n.bucket}-${i}`}
                className="bg-ivory grid grid-cols-12 gap-4 px-6 py-4 items-center"
                data-testid={`admin-digest-notice-${i}`}
              >
                <div className="col-span-1">
                  <span className={`text-xs uppercase tracking-[0.2em] font-medium ${BUCKET_META[n.bucket].cls}`}>
                    {BUCKET_META[n.bucket].label}
                  </span>
                </div>
                <div className="col-span-3 text-sm">
                  <div className="font-serif text-navy">{n.client_name}</div>
                  <div className="text-xs text-slate2 break-all">{n.recipient}</div>
                </div>
                <div className="col-span-4 font-serif italic text-slate2 text-sm leading-snug">
                  {n.subject}
                </div>
                <div className="col-span-2 text-sm text-slate2 font-light">
                  {n.asset_name}
                </div>
                <div className="col-span-1 text-center">
                  {n.certificate_enclosed && (
                    <span
                      className="inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] bg-brass/15 text-brass2 font-medium"
                      data-testid={`admin-digest-cert-badge-${i}`}
                      title="Certificate of IP Stewardship link included"
                    >
                      Cert. Enclosed
                    </span>
                  )}
                </div>
                <div className="col-span-1 text-right text-xs uppercase tracking-[0.2em] text-slate2">
                  {n.days_left}d
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!enabled && (
        <div
          className="mt-12 p-6 border border-transparent bg-linen/50 font-cormorant italic text-slate2 text-base leading-relaxed"
          data-testid="admin-digest-gate-notice"
        >
          <strong className="font-serif not-italic text-navy">Dispatch is currently disabled.</strong>{" "}
          Set <code className="text-brass2">EMAIL_DISPATCH_ENABLED=true</code> and{" "}
          <code className="text-brass2">EMERGENT_EMAIL_KEY</code> in{" "}
          <code className="text-brass2">/app/backend/.env</code>, then restart the backend to activate.
          Until then, no client emails will be sent — only this preview is generated.
          The monthly scheduler is armed and will fire on the 1st at 09:00 UTC, but it also respects this gate.
        </div>
      )}

      {/* Run History */}
      <div className="mt-16">
        <div className="kicker">Run History</div>
        <h3 className="mt-2 font-serif text-2xl text-navy">
          {runs.length === 0 ? "No dispatch runs yet" : `${runs.length} dispatch run(s) recorded`}
        </h3>
        {runs.length > 0 && (
          <div className="mt-6 bg-brass/30 grid grid-cols-1 gap-px" data-testid="admin-digest-runs">
            <div className="bg-linen/60 grid grid-cols-12 gap-4 px-6 py-4 text-xs uppercase tracking-[0.2em] text-slate2 font-medium">
              <div className="col-span-3">When</div>
              <div className="col-span-2">Trigger</div>
              <div className="col-span-2">Notices</div>
              <div className="col-span-2">Sent</div>
              <div className="col-span-2">Firm Alerts</div>
              <div className="col-span-1 text-right">Status</div>
            </div>
            {runs.map((r, i) => (
              <div
                key={`run-${i}`}
                className="bg-ivory grid grid-cols-12 gap-4 px-6 py-4 items-center"
                data-testid={`admin-digest-run-${i}`}
              >
                <div className="col-span-3 text-sm text-slate2 font-light">
                  {new Date(r.started_at).toLocaleString("en-US", {
                    year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                  })}
                </div>
                <div className="col-span-2">
                  <span className={`text-xs uppercase tracking-[0.2em] font-medium ${
                    r.trigger === "scheduled" ? "text-brass2" : "text-navy"
                  }`}>
                    {r.trigger || "manual"}
                  </span>
                </div>
                <div className="col-span-2 font-serif text-navy">{r.total_notices ?? "—"}</div>
                <div className="col-span-2 font-serif text-hunter">{r.sent ?? 0}</div>
                <div className="col-span-2 font-serif text-brass2">{r.firm_alerts ?? 0}</div>
                <div className="col-span-1 text-right">
                  {r.skipped ? (
                    <span className="text-xs uppercase tracking-[0.2em] text-oxblood">Skipped</span>
                  ) : (
                    <span className="text-xs uppercase tracking-[0.2em] text-hunter">OK</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
